"use client";

import {
  useInfiniteQuery,
  useQuery,
  type QueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import type { Group, GroupFeedSort, Page, PostCard } from "@theta/api-client";
import { thetaApi } from "@/lib/api/theta";
import { retryUnlessRefused } from "./queryRetry";

export type { GroupFeedSort };

/**
 * THE GROUP PAGE, ON /api/v1 — spec §4.2
 *
 * Two hooks, both through the generated client. What they replace:
 *
 *   useGroupBySlug  ->  useV1Group      (D8: the slug IS the address, so the
 *                                       by-slug -> id -> feed waterfall is one
 *                                       request, not two)
 *   useGroupFeed    ->  useV1GroupPosts (cursor pages instead of offsets, and
 *                                       `?ifChanged=` instead of a 1 Hz
 *                                       refetch of the whole page)
 *
 * The old hooks stay where they are: the home feed and the post detail page
 * still call them. They go when those screens move (§4.1 step 3).
 */

/**
 * One group, by slug or by id.
 *
 * A 404 is a REAL ANSWER here, not a transport failure: it is what a secret
 * group and a group that does not exist both look like, deliberately and
 * identically (D7). Retrying it would be retrying a correct response, so it is
 * excluded from the retry policy.
 */
/**
 * The cache address of ONE group document, by whatever the caller addressed it
 * with. `{ref}` is the slug OR the id (D8), and the two spellings are two cache
 * entries for one document — which is a fact worth having in one place rather
 * than in nine string literals.
 */
export function groupDocumentKey(ref: string | number) {
  return ["v1", "group", String(ref)] as const;
}

/**
 * A group's custom emoji list.
 *
 * NOTHING IS BROKEN TODAY — five read sites spelled this key by hand and all
 * five happen to agree, because `invalidateCustomEmojis` takes a `number` and
 * every reader has a `number` to give it. This is the trap being closed before
 * it springs, not a bug being fixed.
 *
 * It is worth closing because of how it fails. An invalidation that spells the
 * key even slightly differently does not error and does not warn — it matches
 * no query, the list is never re-read, and the symptom is a count that sits
 * still on screen. That is exactly how OSO-432 and OSO-484 presented, and both
 * were found by a person noticing a stale number rather than by anything in
 * CI. A sixth reader added later, or a reader given a slug where the others
 * have an id, would reopen it silently.
 *
 * `undefined` is accepted because the emoji admin page addresses the group as
 * `group?.id` and reads before the group document has arrived. That key is
 * never fetched under (`enabled: !!group`), so it names a query that cannot
 * hold data — but the factory has to be callable there, or that one site goes
 * back to spelling the key by hand and the point is lost.
 */
export function customEmojisKey(groupId: number | undefined) {
  return ["custom-emojis", groupId] as const;
}

/**
 * RE-READ THE GROUP DOCUMENT — WHICHEVER SPELLING IT IS CACHED UNDER (OSO-432)
 *
 * The admin sidebar's badges are `Group.pendingCounts` — requests, flags and
 * spam reports — so "the menu still says one remaining after I approved it" is
 * always this document not being re-read.
 *
 * EVERY QUEUE INVALIDATED IT BY HAND, AND EACH OF THEM SPELLED THE KEY ITSELF.
 * That works only while the acting screen happens to address the group the same
 * way the sidebar does: the sidebar reads the SLUG out of the pathname, while
 * `{ref}` is deliberately either form, and `ModerationQueue`'s own prop is
 * `string | number`. One call site passing `group.id` — the natural thing to do
 * on a screen that already has the group — invalidates `["v1","group","123"]`
 * while the badge sits under `["v1","group","min-gruppe"]`, and the count goes
 * stale with nothing failing anywhere. `useGroups` had already met this and
 * dropped both spellings by hand.
 *
 * So the prefix is the unit: every cached spelling of every group document is
 * marked stale, and react-query refetches the ones something is actually
 * watching. That is at most a couple of documents — the group whose page is
 * open — and it costs one GET to be right by construction instead of by
 * coincidence.
 */
export function invalidateGroupDocuments(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ["v1", "group"] });
}

/**
 * A group's custom emoji changed: re-read the LIST and the group document.
 *
 * `customEmojiCount` lives on the group document, not on the list — a
 * cursor-paged collection has no `total` (§1.3/D4) — and it is what decides
 * whether the Emojis tab is offered to a member who cannot add any
 * (`GroupHeader`). Three call sites invalidated the list alone: the emoji
 * admin page's create and delete, and the inline "Tilføj emoji" in
 * `EmojiPicker`. That is the same shape as the shelf count in OSO-432 and
 * OSO-484, so the pair is one function rather than a rule to remember at each
 * new call site.
 */
export function invalidateCustomEmojis(queryClient: QueryClient, groupId: number) {
  void queryClient.invalidateQueries({ queryKey: customEmojisKey(groupId) });
  invalidateGroupDocuments(queryClient);
}

/**
 * `seed` is the copy `app/grupper/[slug]/layout.tsx` already read on the server,
 * and passing it is what lets everything under the slug render into the HTML
 * instead of behind that layout's loading gate. Omitted — or `null`, which is
 * what a 404 or a failed server read comes back as — the hook fetches exactly
 * as it always did.
 *
 * Only the layout passes it. Every other caller reads the same cache entry the
 * layout's seed created, because it is the same key.
 */
export function useV1Group(ref: string, seed?: Group | null) {
  return useQuery({
    queryKey: groupDocumentKey(ref),
    queryFn: () => thetaApi.group(ref),
    enabled: ref.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused,
    ...(seed ? { initialData: seed } : {}),
  });
}

/** The cadence the server hints at when it has not said otherwise (§1.10). */
const FALLBACK_POLL_MS = 1_000;

/**
 * A group's feed, cursor-paged and conditionally read.
 *
 * WHY THERE IS A CACHE IN HERE
 * `?ifChanged=` answers `{unchanged: true}` with NO DOCUMENT — that is the
 * whole point of it, and it is what makes a 1 Hz poll on the most expensive
 * read in the app affordable. But a react-query `queryFn` must return the
 * page, so "nothing changed" has to resolve to the page we already had.
 *
 * The map below holds the last `state` token and the last page per cursor, so
 * an unchanged tick returns the SAME OBJECT REFERENCE and react-query does not
 * re-render. It lives in a ref rather than in module scope because two group
 * pages must not share one; it is keyed by group and sort as well as cursor
 * because a token is only valid for the document it came from — the server
 * folds all three into the token and would simply miss, but missing on every
 * tick would quietly turn the conditional read back into a full one.
 *
 * The poll is bounded to the FIRST page, exactly as the old hook bounded it:
 * `useInfiniteQuery` refetches every loaded page on each tick, and somebody
 * who has scrolled into history is reading, not watching for new posts.
 *
 * `seed` IS THE FIRST PAGE AS THE SERVER READ IT (OSO-687), and it is what puts
 * the cards in the HTML instead of behind the skeleton. Like the post page's
 * seed it does NOT save the request — `staleTime: 0` below is what keeps the
 * poll's refetch-on-focus honest — but the re-read happens behind cards that
 * are already painted.
 *
 * THE CALLER GUARDS WHEN IT APPLIES, and it takes two conditions rather than
 * the obvious one. This hook is keyed by sort while the sort lives in client
 * state, so a page read as "newest" and applied under another sort would be the
 * wrong list under the right key; and `initialData` is consulted whenever
 * react-query CREATES the entry, not only on first mount, so a sort the reader
 * left long enough to be garbage-collected would be re-seeded from a stale
 * server read on return. `GroupFeedClient` compares the sort AND retires the
 * seed once the control is touched; its note has the full reasoning.
 */
export function useV1GroupPosts(
  ref: string,
  sort: GroupFeedSort,
  options: { enabled?: boolean; seed?: Page<PostCard> | null } = {},
) {
  const seen = useRef(new Map<string, { state: string; page: Page<PostCard> }>());

  return useInfiniteQuery({
    queryKey: ["v1", "group-posts", ref, sort],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const key = `${ref}|${sort}|${pageParam ?? ""}`;
      const previous = seen.current.get(key);

      const result = await thetaApi.groupPosts(ref, {
        sort,
        cursor: pageParam,
        ...(previous ? { ifChanged: previous.state } : {}),
      });

      if (result.unchanged) {
        // `previous` cannot be missing — we only send a token we stored — but
        // an empty page is a safer answer than a crash if it ever is.
        return previous?.page ?? { items: [], nextCursor: null, prevCursor: null, hasMore: false };
      }

      seen.current.set(key, { state: result.state ?? "", page: result.data });
      return result.data;
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    enabled: ref.length > 0 && (options.enabled ?? true),
    retry: retryUnlessRefused,
    /**
     * ONE page and ONE page param, because that is what the server read. The
     * `pageParams` entry must be the `initialPageParam` above — `null`, the
     * cursor that means "the first page" — or `fetchNextPage` would compute the
     * next cursor from a page it thinks it reached some other way.
     */
    ...(options.seed
      ? { initialData: { pages: [options.seed], pageParams: [null] } }
      : {}),

    // The server owns the cadence (D12): changing it is a backend deploy
    // rather than an App Store release. `useLivePolling`'s visibility gate is
    // reproduced here because a backgrounded tab must not poll at all.
    refetchInterval: (query) =>
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden" &&
      (query.state.data?.pages.length ?? 0) <= 1
        ? FALLBACK_POLL_MS
        : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

/** The query key the composer invalidates after a successful write. */
export function groupPostsKey(ref: string): (string | number)[] {
  return ["v1", "group-posts", ref];
}

/**
 * EVERY group's posts, for a caller that does not know which group it is in.
 *
 * A comment carries no slug — `CommentItem` is handed a post id and nothing
 * about where the post lives — so a write from there has to invalidate the
 * prefix and let react-query match every loaded group feed. Spelled out here
 * rather than as a `["v1", "group-posts"]` literal at each call site, so that
 * changing the key above cannot silently turn those into no-ops.
 */
export const groupPostsPrefix = ["v1", "group-posts"];
